import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
    LayoutDashboard, FolderOpen, Calendar, Flame, Zap, Award,
    AlertCircle, Check, Eye, ArrowUpRight, Target, ShieldCheck,
    Plus, Download, History, Settings, Save, GitCompare, Clock, CheckCircle2, PartyPopper, ChevronDown,
    UserCheck, DraftingCompass, Users as UsersCode
} from "lucide-react";
import { cn } from "../../utils/cn";
import { calculateVelocityMetrics, isEpicStalled } from "../../utils/velocity";
import { calculateOwnerMetrics, getLoadLevel, LaneScope, TeamRole, calculateProjectRoleCounts } from "../../utils/teamMetrics";
import { HealthTrendChart } from "../ui/TrendSparkline";
import { Epic, Session, Project, User } from "../../types";

interface ExecutiveViewProps {
    epics: Epic[];
    sessions: Session[];
    onSelectEpic?: (id: string) => void;
    onAddEpic?: () => void;
    onExport?: () => void;
    onViewHistory?: () => void;
    onSaveSession?: () => void;
    onCompare?: () => void;
    onToggleSettings?: () => void;
    projects?: Project[];
    users?: User[];
}

interface AnimatedCounterProps {
    value: number | string;
    suffix?: string;
}

/**
 * AnimatedCounter - Displays animated numbers
 */
function AnimatedCounter({ value, suffix = "" }: AnimatedCounterProps) {
    return (
        <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="tabular-nums"
        >
            {typeof value === "number" ? value.toFixed(value % 1 === 0 ? 0 : 1) : value}{suffix}
        </motion.span>
    );
}

/**
 * ExecutiveView - Strategic dashboard for executives and SGMs
 */
export function ExecutiveView({
    epics,
    sessions,
    onSelectEpic,
    onAddEpic,
    onExport,
    onViewHistory,
    onSaveSession,
    onCompare,
    onToggleSettings,
    projects,
    users = []
}: ExecutiveViewProps) {
    const [groupByProject, setGroupByProject] = useState(false);
    const [teamPerformanceExpanded, setTeamPerformanceExpanded] = useState(false);
    const [laneScope, setLaneScope] = useState<LaneScope>('active');
    const [teamRole, setTeamRole] = useState<TeamRole>('owner');

    // Render Owner Chips Helper
    const renderOwners = (so: string | string[] | undefined) => {
        if (!so) return <span className="text-siemens-text-soft">—</span>;

        const rawIds: string[] = [];
        const inputs = Array.isArray(so) ? so : [so];
        inputs.forEach(input => {
            // Split by comma or semicolon to handle legacy string formats
            // e.g. "id1, id2" or "Name Name, Other Name"
            rawIds.push(...input.split(/[,;]+/).map(s => s.trim()).filter(Boolean));
        });

        // Match EpicCard logic: Map to valid users
        const assignedOwners = rawIds
            .map(id => users?.find(u => u.id === id))
            .filter(Boolean) as User[];

        // If we found valid users, show them as chips
        if (assignedOwners.length > 0) {
            // Unique owners only
            const uniqueUsers = Array.from(new Map(assignedOwners.map(u => [u.id, u])).values());

            return (
                <div className="flex flex-wrap gap-1.5">
                    {uniqueUsers.map((user, idx) => (
                        <span key={`u-${user.id}-${idx}`} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-siemens-petrol/10 text-siemens-petrol dark:bg-siemens-petrol-light/10 dark:text-siemens-petrol-light whitespace-nowrap">
                            <div className="w-4 h-4 rounded-full flex items-center justify-center bg-white/50 dark:bg-black/20 overflow-hidden">
                                {user.photoUrl ? (
                                    <img src={user.photoUrl} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="text-[8px] font-bold">{(user.displayName || user.name).substring(0, 2).toUpperCase()}</div>
                                )}
                            </div>
                            {user.displayName}
                        </span>
                    ))}
                </div>
            );
        }

        // Fallback: If NO valid users found (legacy data?), render raw strings as simple text or basic chips
        // Ideally we ignore the "garbage" strings if we have valid users.

        // Filter out strings that might still look like garbage or just display them as-is since we split them
        const cleanStrings = Array.from(new Set(rawIds)); // Deduplicate strings

        if (cleanStrings.length > 0) {
            return (
                <div className="flex flex-wrap gap-1.5">
                    {cleanStrings.map((str, idx) => (
                        <span key={`s-${str}-${idx}`} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 whitespace-nowrap">
                            <div className="w-4 h-4 rounded-full flex items-center justify-center bg-slate-200 dark:bg-slate-700">
                                <span className="text-[10px]">?</span>
                            </div>
                            {str}
                        </span>
                    ))}
                </div>
            );
        }

        return <span className="text-siemens-text-soft">—</span>;
    };

    // Calculate executive-level metrics
    const metrics = useMemo(() => {
        const total = epics.length;
        const nowCount = epics.filter(e => e.lane === "Now").length;
        const nextCount = epics.filter(e => e.lane === "Next").length;
        const laterCount = epics.filter(e => e.lane === "Later").length;
        const unassigned = epics.filter(e => e.lane === "Unassigned").length;
        const doneCount = epics.filter(e => e.lane === "Done").length;
        const sgmCount = epics.filter(e => e.sgm).length;

        const totalWsjf = epics.reduce((sum, e) => sum + (e.wsjf || 0), 0);
        const avgWsjf = total > 0 ? totalWsjf / total : 0;

        // Portfolio progress
        const totalProgress = epics.reduce((sum, e) => sum + (e.progress || 0), 0);
        const avgProgress = total > 0 ? Math.round(totalProgress / total) : 0;

        // Recently completed (last 30 days)
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const recentlyCompleted = epics
            .filter(e => e.lane === 'Done' && e.completedAt && new Date(e.completedAt) >= thirtyDaysAgo)
            .sort((a, b) => new Date(b.completedAt!).getTime() - new Date(a.completedAt!).getTime());

        // Priority alerts
        const highPriorityUnassigned = epics.filter(e => e.lane === "Unassigned" && (e.wsjf || 0) > 10);
        const sgmNeedingAttention = epics.filter(e => e.sgm && e.lane !== "Now" && e.lane !== "Done");
        const topOpportunities = [...epics].filter(e => e.lane !== 'Done').sort((a, b) => (b.wsjf || 0) - (a.wsjf || 0)).slice(0, 5);

        // Health score calculation (now includes Done ratio)
        const assignedRatio = total > 0 ? ((total - unassigned) / total) * 100 : 0;
        const completionBonus = total > 0 ? (doneCount / total) * 20 : 0; // Up to 20 points for completion
        const balanceScore = Math.min(100, (nowCount > 0 ? 25 : 0) + (nextCount > 0 ? 25 : 0) + (laterCount > 0 ? 15 : 0) + (unassigned < 3 ? 15 : 0) + completionBonus);
        const healthScore = Math.round((assignedRatio + balanceScore) / 2);

        // Velocity metrics
        const stalledCount = epics.filter(e => e.lane !== 'Done' && isEpicStalled(e)).length;

        return {
            total, nowCount, nextCount, laterCount, unassigned, doneCount, sgmCount,
            avgWsjf, avgProgress, recentlyCompleted, highPriorityUnassigned, sgmNeedingAttention,
            topOpportunities, healthScore, stalledCount
        };
    }, [epics]);

    // Project-grouped stats
    const projectStats = useMemo(() => {
        if (!groupByProject || !projects) return null;

        return projects.map(project => {
            const projectEpics = epics.filter(e => String(e.projectId || 'project-legacy') === String(project.id));
            const total = projectEpics.length;
            const nowCount = projectEpics.filter(e => e.lane === "Now").length;
            const doneCount = projectEpics.filter(e => e.lane === "Done").length;
            const avgWsjf = total > 0 ? projectEpics.reduce((sum, e) => sum + (e.wsjf || 0), 0) / total : 0;
            const sgmCount = projectEpics.filter(e => e.sgm).length;
            const progressPercent = total > 0 ? Math.round((doneCount / total) * 100) : 0;
            const counts = calculateProjectRoleCounts(projectEpics, project, users);

            return {
                ...project,
                epicCount: total,
                nowCount,
                doneCount,
                progressPercent,
                avgWsjf,
                sgmCount,
                pmCount: counts.pmCount,
                ownerCount: counts.ownerCount,
                architectCount: counts.architectCount,
                topEpics: [...projectEpics].filter(e => e.lane !== 'Done').sort((a, b) => (b.wsjf || 0) - (a.wsjf || 0)).slice(0, 3)
            };
        }).filter(p => p.epicCount > 0).sort((a, b) => b.avgWsjf - a.avgWsjf);
    }, [epics, projects, groupByProject]);

    return (
        <div className="p-4 md:p-8 space-y-6 md:space-y-8 overflow-y-auto h-full bg-gradient-to-br from-slate-50 to-white dark:from-[#0a0a12] dark:to-[#13131a]">

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-black text-siemens-deep-blue dark:text-white">
                        Executive Overview
                    </h1>
                    <p className="text-sm text-siemens-text-soft mt-1">
                        Project health and priority insights at a glance
                    </p>
                </div>
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => setGroupByProject(!groupByProject)}
                        className={cn(
                            "flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold transition-all",
                            groupByProject
                                ? "bg-siemens-petrol text-white"
                                : "bg-slate-100 dark:bg-slate-800 text-siemens-text-soft hover:bg-slate-200 dark:hover:bg-slate-700"
                        )}
                    >
                        <FolderOpen className="w-4 h-4" />
                        Group by Project
                    </button>
                    <div className="flex items-center gap-2 text-xs text-siemens-text-soft">
                        <Calendar className="w-4 h-4" />
                        <span>Last updated: {new Date().toLocaleDateString()}</span>
                    </div>
                </div>
            </div>

            {/* Hero Stats Grid - 6 columns */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    className="relative overflow-hidden rounded-xl bg-gradient-to-br from-siemens-petrol to-siemens-petrol-dark p-4 md:p-5 text-white">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <LayoutDashboard className="w-6 h-6 mb-2 opacity-80" />
                    <div className="text-3xl md:text-4xl font-black"><AnimatedCounter value={metrics.total} /></div>
                    <div className="text-xs font-medium opacity-80 mt-1">Total Epics</div>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                    className="relative overflow-hidden rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 p-4 md:p-5 text-white">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <CheckCircle2 className="w-6 h-6 mb-2 opacity-80" />
                    <div className="text-3xl md:text-4xl font-black"><AnimatedCounter value={metrics.doneCount} /></div>
                    <div className="text-xs font-medium opacity-80 mt-1">Completed</div>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                    className="relative overflow-hidden rounded-xl bg-gradient-to-br from-siemens-feedback-green to-emerald-600 p-4 md:p-5 text-white">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <Flame className="w-6 h-6 mb-2 opacity-80" />
                    <div className="text-3xl md:text-4xl font-black"><AnimatedCounter value={metrics.nowCount} /></div>
                    <div className="text-xs font-medium opacity-80 mt-1">In Flight</div>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                    className="relative overflow-hidden rounded-xl bg-gradient-to-br from-siemens-feedback-orange to-orange-600 p-4 md:p-5 text-white">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <Zap className="w-6 h-6 mb-2 opacity-80" />
                    <div className="text-3xl md:text-4xl font-black"><AnimatedCounter value={metrics.avgWsjf} /></div>
                    <div className="text-xs font-medium opacity-80 mt-1">Avg WSJF</div>
                </motion.div>

                {/* Stalled Epics */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                    className={cn(
                        "relative overflow-hidden rounded-xl p-4 md:p-5 text-white",
                        metrics.stalledCount === 0 ? "bg-gradient-to-br from-slate-400 to-slate-500" :
                            metrics.stalledCount <= 3 ? "bg-gradient-to-br from-amber-500 to-amber-600" :
                                "bg-gradient-to-br from-red-500 to-red-700"
                    )}>
                    <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <Clock className="w-6 h-6 mb-2 opacity-80" />
                    <div className="text-3xl md:text-4xl font-black"><AnimatedCounter value={metrics.stalledCount} /></div>
                    <div className="text-xs font-medium opacity-80 mt-1">Stalled ({">"}30d)</div>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
                    className={cn(
                        "relative overflow-hidden rounded-xl p-4 md:p-5 text-white",
                        metrics.healthScore >= 70 ? "bg-gradient-to-br from-siemens-feedback-green to-emerald-600" :
                            metrics.healthScore >= 40 ? "bg-gradient-to-br from-siemens-feedback-orange to-orange-600" :
                                "bg-gradient-to-br from-red-500 to-red-700"
                    )}>
                    <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <Award className="w-6 h-6 mb-2 opacity-80" />
                    <div className="text-3xl md:text-4xl font-black"><AnimatedCounter value={metrics.healthScore} suffix="%" /></div>
                    <div className="text-xs font-medium opacity-80 mt-1">Project Health</div>
                </motion.div>
            </div>

            {/* Project Breakdown (when grouped) */}
            {groupByProject && projectStats && projectStats.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                    <div className="flex items-center gap-2">
                        <FolderOpen className="w-5 h-5 text-siemens-petrol" />
                        <h3 className="font-bold text-siemens-deep-blue dark:text-white">Project Breakdown</h3>
                        <span className="text-xs text-siemens-text-soft">({projectStats.length} projects)</span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {projectStats.map((project, idx) => (
                            <motion.div key={project.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}
                                className="rounded-xl bg-white dark:bg-[#1a1a24] border border-siemens-deep-blue/8 dark:border-[#3d3d4d] p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <span className={cn("w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                                            idx === 0 ? "bg-siemens-petrol text-white" : idx === 1 ? "bg-siemens-petrol/60 text-white" : "bg-siemens-petrol/20 text-siemens-petrol"
                                        )}>{idx + 1}</span>
                                        <h4 className="font-bold text-siemens-deep-blue dark:text-white truncate">{project.name || project.id}</h4>
                                    </div>
                                    {project.sgmCount > 0 && (
                                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-siemens-petrol/10 text-siemens-petrol">{project.sgmCount} SGM</span>
                                    )}
                                </div>

                                {/* Progress Bar */}
                                <div className="mb-3">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-[10px] font-bold uppercase text-siemens-text-soft">Progress</span>
                                        <span className={cn(
                                            "text-xs font-black",
                                            project.progressPercent >= 100 ? "text-emerald-500" :
                                                project.progressPercent >= 50 ? "text-siemens-petrol" : "text-slate-500"
                                        )}>{project.progressPercent}%</span>
                                    </div>
                                    <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: `${project.progressPercent}%` }}
                                            transition={{ duration: 0.6, delay: 0.2 + idx * 0.05 }}
                                            className={cn(
                                                "h-full rounded-full",
                                                project.progressPercent >= 100 ? "bg-emerald-500" :
                                                    project.progressPercent >= 50 ? "bg-siemens-petrol" : "bg-amber-500"
                                            )}
                                        />
                                    </div>
                                </div>

                                {/* Role Counts */}
                                <div className="flex items-center justify-between mb-3 px-1 py-1 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-100 dark:border-slate-700/50">
                                    {/* PM */}
                                    <div className="flex items-center gap-1 px-2" title="Project Managers">
                                        <UsersCode className="w-3 h-3 text-blue-500" />
                                        <span className="text-xs font-bold text-siemens-deep-blue dark:text-white">{project.pmCount}</span>
                                    </div>
                                    <div className="w-px h-3 bg-slate-200 dark:bg-slate-700" />
                                    {/* Owner */}
                                    <div className="flex items-center gap-1 px-2" title="Service Owners">
                                        <UserCheck className="w-3 h-3 text-indigo-500" />
                                        <span className="text-xs font-bold text-siemens-deep-blue dark:text-white">{project.ownerCount}</span>
                                    </div>
                                    <div className="w-px h-3 bg-slate-200 dark:bg-slate-700" />
                                    {/* Architect */}
                                    <div className="flex items-center gap-1 px-2" title="Solution Architects">
                                        <DraftingCompass className="w-3 h-3 text-purple-500" />
                                        <span className="text-xs font-bold text-siemens-deep-blue dark:text-white">{project.architectCount}</span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-4 gap-2 mb-3">
                                    <div className="text-center p-2 rounded bg-slate-50 dark:bg-slate-800">
                                        <div className="text-lg font-black text-siemens-deep-blue dark:text-white">{project.epicCount}</div>
                                        <div className="text-[10px] text-siemens-text-soft">Epics</div>
                                    </div>
                                    <div className="text-center p-2 rounded bg-slate-50 dark:bg-slate-800">
                                        <div className="text-lg font-black text-emerald-500">{project.doneCount}</div>
                                        <div className="text-[10px] text-siemens-text-soft">Done</div>
                                    </div>
                                    <div className="text-center p-2 rounded bg-slate-50 dark:bg-slate-800">
                                        <div className="text-lg font-black text-siemens-feedback-green">{project.nowCount}</div>
                                        <div className="text-[10px] text-siemens-text-soft">In Flight</div>
                                    </div>
                                    <div className="text-center p-2 rounded bg-slate-50 dark:bg-slate-800">
                                        <div className="text-lg font-black text-siemens-petrol">{(project.avgWsjf || 0).toFixed(1)}</div>
                                        <div className="text-[10px] text-siemens-text-soft">WSJF</div>
                                    </div>
                                </div>
                                {project.topEpics.length > 0 && (
                                    <div className="space-y-1">
                                        <div className="text-[10px] font-bold uppercase text-siemens-text-soft">Top Epics</div>
                                        {project.topEpics.map(epic => (
                                            <button key={epic.id} onClick={() => onSelectEpic?.(epic.id)}
                                                className="w-full flex items-center justify-between text-left p-2 rounded hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                                                <span className="text-xs text-siemens-deep-blue dark:text-white truncate pr-2">{epic.title}</span>
                                                <span className="text-xs font-bold text-siemens-petrol flex-shrink-0">{epic.wsjf?.toFixed(1)}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </motion.div>
                        ))
                        }
                    </div >
                </motion.div >
            )}

            {/* Alerts & Distribution Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                {/* Priority Alerts */}
                <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}
                    className="rounded-xl bg-white dark:bg-[#1a1a24] border border-siemens-deep-blue/8 dark:border-[#3d3d4d] overflow-hidden">
                    <div className="px-5 py-4 border-b border-siemens-deep-blue/5 dark:border-[#3d3d4d] flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-siemens-feedback-orange" />
                            <h3 className="font-bold text-siemens-deep-blue dark:text-white">Priority Alerts</h3>
                        </div>
                        <span className="text-xs font-bold text-white bg-siemens-feedback-orange px-2 py-1 rounded-full">
                            {metrics.highPriorityUnassigned.length + metrics.sgmNeedingAttention.length}
                        </span>
                    </div>
                    <div className="divide-y divide-siemens-deep-blue/5 dark:divide-[#3d3d4d] max-h-64 overflow-y-auto">
                        {metrics.highPriorityUnassigned.length === 0 && metrics.sgmNeedingAttention.length === 0 ? (
                            <div className="p-8 text-center">
                                <Check className="w-10 h-10 text-siemens-feedback-green mx-auto mb-2" />
                                <p className="text-sm font-medium text-siemens-text-soft">All clear! No priority alerts.</p>
                            </div>
                        ) : (
                            <>
                                {metrics.highPriorityUnassigned.map(epic => {
                                    const project = projects?.find(p => p.id === epic.projectId);
                                    return (
                                        <button key={epic.id} onClick={() => onSelectEpic?.(epic.id)}
                                            className="w-full p-4 flex items-start gap-3 hover:bg-siemens-deep-blue-8/50 dark:hover:bg-white/5 transition-colors text-left">
                                            <div className="w-8 h-8 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                                                <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-0.5">
                                                    {project && (
                                                        <span className="text-[10px] uppercase font-bold text-siemens-text-soft bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                                                            {project.name}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm font-semibold text-siemens-deep-blue dark:text-white truncate">{epic.title}</p>
                                                <p className="text-xs text-siemens-text-soft mt-0.5">High priority but unassigned • WSJF: {epic.wsjf?.toFixed(1)}</p>
                                            </div>
                                            <ArrowUpRight className="w-4 h-4 text-siemens-text-soft flex-shrink-0" />
                                        </button>
                                    );
                                })}
                                {metrics.sgmNeedingAttention.map(epic => {
                                    const project = projects?.find(p => p.id === epic.projectId);
                                    return (
                                        <button key={epic.id} onClick={() => onSelectEpic?.(epic.id)}
                                            className="w-full p-4 flex items-start gap-3 hover:bg-siemens-deep-blue-8/50 dark:hover:bg-white/5 transition-colors text-left">
                                            <div className="w-8 h-8 rounded-lg bg-siemens-feedback-blue/10 flex items-center justify-center flex-shrink-0">
                                                <Eye className="w-4 h-4 text-siemens-feedback-blue" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-0.5">
                                                    {project && (
                                                        <span className="text-[10px] uppercase font-bold text-siemens-text-soft bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                                                            {project.name}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm font-semibold text-siemens-deep-blue dark:text-white truncate">{epic.title}</p>
                                                <p className="text-xs text-siemens-text-soft mt-0.5">SGM needs attention • Lane: {epic.lane}</p>
                                            </div>
                                            <ArrowUpRight className="w-4 h-4 text-siemens-text-soft flex-shrink-0" />
                                        </button>
                                    );
                                })}
                            </>
                        )}
                    </div>
                </motion.div>

                {/* Portfolio Status (formerly Project Distribution) */}
                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }}
                    className="rounded-xl bg-white dark:bg-[#1a1a24] border border-siemens-deep-blue/8 dark:border-[#3d3d4d] p-5">
                    <h3 className="font-bold text-siemens-deep-blue dark:text-white mb-4">Portfolio Status</h3>
                    <div className="h-6 rounded-full overflow-hidden flex mb-4 bg-slate-100 dark:bg-slate-800">
                        {metrics.doneCount > 0 && (
                            <motion.div initial={{ width: 0 }} animate={{ width: `${(metrics.doneCount / metrics.total) * 100}%` }}
                                transition={{ duration: 0.8, delay: 0.55 }} className="bg-emerald-500 h-full" />
                        )}
                        {metrics.nowCount > 0 && (
                            <motion.div initial={{ width: 0 }} animate={{ width: `${(metrics.nowCount / metrics.total) * 100}%` }}
                                transition={{ duration: 0.8, delay: 0.6 }} className="bg-siemens-feedback-green h-full" />
                        )}
                        {metrics.nextCount > 0 && (
                            <motion.div initial={{ width: 0 }} animate={{ width: `${(metrics.nextCount / metrics.total) * 100}%` }}
                                transition={{ duration: 0.8, delay: 0.7 }} className="bg-siemens-feedback-orange h-full" />
                        )}
                        {metrics.laterCount > 0 && (
                            <motion.div initial={{ width: 0 }} animate={{ width: `${(metrics.laterCount / metrics.total) * 100}%` }}
                                transition={{ duration: 0.8, delay: 0.8 }} className="bg-siemens-feedback-blue h-full" />
                        )}
                        {metrics.unassigned > 0 && (
                            <motion.div initial={{ width: 0 }} animate={{ width: `${(metrics.unassigned / metrics.total) * 100}%` }}
                                transition={{ duration: 0.8, delay: 0.9 }} className="bg-slate-400 h-full" />
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        {[
                            { label: "Done", count: metrics.doneCount, color: "bg-emerald-500" },
                            { label: "Now", count: metrics.nowCount, color: "bg-siemens-feedback-green" },
                            { label: "Next", count: metrics.nextCount, color: "bg-siemens-feedback-orange" },
                            { label: "Later", count: metrics.laterCount, color: "bg-siemens-feedback-blue" },
                            { label: "Unassigned", count: metrics.unassigned, color: "bg-slate-400" },
                        ].map(item => (
                            <div key={item.label} className="flex items-center gap-2">
                                <div className={cn("w-3 h-3 rounded-full", item.color)} />
                                <span className="text-sm text-siemens-deep-blue dark:text-siemens-text-soft">{item.label}</span>
                                <span className="ml-auto text-sm font-bold text-siemens-deep-blue dark:text-white">{item.count}</span>
                            </div>
                        ))}
                    </div>
                    {metrics.sgmCount > 0 && (
                        <div className="mt-4 p-3 rounded-lg bg-siemens-petrol/10 dark:bg-siemens-petrol/20 flex items-center gap-3">
                            <ShieldCheck className="w-5 h-5 text-siemens-petrol" />
                            <div>
                                <p className="text-sm font-semibold text-siemens-deep-blue dark:text-white">{metrics.sgmCount} SGM Items</p>
                                <p className="text-xs text-siemens-text-soft">Require Service Group Manager direction</p>
                            </div>
                        </div>
                    )}
                </motion.div>
            </div>

            {/* Health Trend Section */}
            {
                sessions && sessions.length >= 2 && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
                        className="rounded-xl bg-white dark:bg-[#1a1a24] border border-siemens-deep-blue/8 dark:border-[#3d3d4d] p-5">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <Award className="w-5 h-5 text-siemens-petrol" />
                                <h3 className="font-bold text-siemens-deep-blue dark:text-white">Project Health Trend</h3>
                            </div>
                            <span className="text-xs text-siemens-text-soft">Last {Math.min(sessions.length, 12)} sessions</span>
                        </div>
                        <HealthTrendChart sessions={sessions} width={Math.min(600, window.innerWidth - 100)} height={80} />
                    </motion.div>
                )
            }

            {/* Recently Completed Section */}
            {
                metrics.recentlyCompleted.length > 0 && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.58 }}
                        className="rounded-xl bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/10 border border-emerald-200 dark:border-emerald-800/30 overflow-hidden">
                        <div className="px-5 py-4 border-b border-emerald-200 dark:border-emerald-800/30 flex items-center gap-2">
                            <PartyPopper className="w-5 h-5 text-emerald-600" />
                            <h3 className="font-bold text-emerald-800 dark:text-emerald-300">Recently Completed</h3>
                            <span className="ml-auto text-xs font-medium text-emerald-600 dark:text-emerald-400">{metrics.recentlyCompleted.length} in last 30 days</span>
                        </div>
                        <div className="divide-y divide-emerald-200 dark:divide-emerald-800/30 max-h-64 overflow-y-auto">
                            {metrics.recentlyCompleted.slice(0, 5).map((epic) => (
                                <button key={epic.id} onClick={() => onSelectEpic?.(epic.id)}
                                    className="w-full p-4 flex items-center gap-4 hover:bg-emerald-100/50 dark:hover:bg-emerald-900/20 transition-colors text-left">
                                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                                        <CheckCircle2 className="w-4 h-4 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-bold text-emerald-900 dark:text-emerald-200 truncate">{epic.title}</p>
                                        <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
                                            Completed {new Date(epic.completedAt!).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <span className="text-lg font-black text-emerald-700 dark:text-emerald-300 tabular-nums">
                                            {(epic.wsjf || 0).toFixed(1)}
                                        </span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </motion.div>
                )
            }

            {/* Top Opportunities */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
                className="rounded-xl bg-white dark:bg-[#1a1a24] border border-siemens-deep-blue/8 dark:border-[#3d3d4d] overflow-hidden">
                <div className="px-5 py-4 border-b border-siemens-deep-blue/5 dark:border-[#3d3d4d] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Target className="w-5 h-5 text-siemens-petrol" />
                        <h3 className="font-bold text-siemens-deep-blue dark:text-white">Top 5 Opportunities</h3>
                    </div>
                    <span className="text-xs text-siemens-text-soft">Ranked by WSJF Score</span>
                </div>

                {/* Desktop Table */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-siemens-deep-blue-8/50 dark:bg-[#13131a]">
                            <tr>
                                <th className="px-5 py-3 text-left text-xs font-bold text-siemens-text-soft uppercase tracking-wider w-12">#</th>
                                <th className="px-5 py-3 text-left text-xs font-bold text-siemens-text-soft uppercase tracking-wider">Epic</th>
                                <th className="px-5 py-3 text-left text-xs font-bold text-siemens-text-soft uppercase tracking-wider w-24">Lane</th>
                                <th className="px-5 py-3 text-left text-xs font-bold text-siemens-text-soft uppercase tracking-wider w-32">Owner</th>
                                <th className="px-5 py-3 text-right text-xs font-bold text-siemens-text-soft uppercase tracking-wider w-24">WSJF</th>
                                <th className="px-5 py-3 w-12"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-siemens-deep-blue/5 dark:divide-[#3d3d4d]">
                            {metrics.topOpportunities.map((epic, idx) => {
                                const project = projects?.find(p => p.id === epic.projectId);
                                return (
                                    <tr key={epic.id} className="hover:bg-siemens-deep-blue-8/30 dark:hover:bg-white/5 transition-colors">
                                        <td className="px-5 py-4">
                                            <span className={cn("w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white",
                                                idx === 0 ? "bg-amber-500" : idx === 1 ? "bg-slate-400" : idx === 2 ? "bg-amber-700" : "bg-slate-300"
                                            )}>{idx + 1}</span>
                                        </td>
                                        <td className="px-5 py-4">
                                            <div className="flex flex-col">
                                                {project && (
                                                    <span className="text-[10px] uppercase font-bold text-siemens-text-soft mb-1">
                                                        {project.name}
                                                    </span>
                                                )}
                                                <p className="text-sm font-semibold text-siemens-deep-blue dark:text-white truncate max-w-md">{epic.title}</p>
                                            </div>
                                        </td>
                                        <td className="px-5 py-4">
                                            <span className={cn("px-2 py-1 rounded text-xs font-bold",
                                                epic.lane === "Now" ? "bg-siemens-feedback-green/10 text-siemens-feedback-green" :
                                                    epic.lane === "Next" ? "bg-siemens-feedback-orange/10 text-siemens-feedback-orange" :
                                                        epic.lane === "Later" ? "bg-siemens-feedback-blue/10 text-siemens-feedback-blue" :
                                                            "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400"
                                            )}>{epic.lane}</span>
                                        </td>
                                        <td className="px-5 py-4 text-sm text-siemens-text-soft truncate max-w-[250px]">{renderOwners(epic.so)}</td>
                                        <td className="px-5 py-4 text-right">
                                            <span className="text-lg font-black text-siemens-petrol">{epic.wsjf?.toFixed(1)}</span>
                                        </td>
                                        <td className="px-5 py-4">
                                            <button onClick={() => onSelectEpic?.(epic.id)} className="p-2 rounded-lg hover:bg-siemens-petrol/10 transition-colors">
                                                <ArrowUpRight className="w-4 h-4 text-siemens-petrol" />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Mobile Cards */}
                <div className="md:hidden divide-y divide-siemens-deep-blue/5 dark:divide-[#3d3d4d]">
                    {metrics.topOpportunities.map((epic, idx) => {
                        const project = projects?.find(p => p.id === epic.projectId);
                        return (
                            <button key={epic.id} onClick={() => onSelectEpic?.(epic.id)}
                                className="w-full p-4 flex items-center gap-3 hover:bg-siemens-deep-blue-8/30 dark:hover:bg-white/5 transition-colors text-left">
                                <span className={cn("w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0",
                                    idx === 0 ? "bg-amber-500" : idx === 1 ? "bg-slate-400" : idx === 2 ? "bg-amber-700" : "bg-slate-300"
                                )}>{idx + 1}</span>
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-col">
                                        {project && (
                                            <span className="text-[10px] uppercase font-bold text-siemens-text-soft mb-0.5">
                                                {project.name}
                                            </span>
                                        )}
                                        <p className="text-sm font-semibold text-siemens-deep-blue dark:text-white truncate">{epic.title}</p>
                                    </div>
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className={cn("px-1.5 py-0.5 rounded text-[10px] font-bold",
                                            epic.lane === "Now" ? "bg-siemens-feedback-green/10 text-siemens-feedback-green" :
                                                epic.lane === "Next" ? "bg-siemens-feedback-orange/10 text-siemens-feedback-orange" :
                                                    epic.lane === "Later" ? "bg-siemens-feedback-blue/10 text-siemens-feedback-blue" :
                                                        "bg-slate-100 dark:bg-slate-800 text-slate-500"
                                        )}>{epic.lane}</span>
                                        <span className="text-xs text-siemens-text-soft">{renderOwners(epic.so)}</span>
                                    </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                    <span className="text-lg font-black text-siemens-petrol">{epic.wsjf?.toFixed(1)}</span>
                                    <p className="text-[10px] text-siemens-text-soft">WSJF</p>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="bg-white dark:bg-[#1a1a24] rounded-2xl border border-siemens-deep-blue/5 dark:border-[#3d3d4d] overflow-hidden"
            >
                <button
                    onClick={() => setTeamPerformanceExpanded(!teamPerformanceExpanded)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-siemens-deep-blue-8/20 dark:hover:bg-white/5 transition-colors"
                >
                    <div>
                        <h2 className="text-lg font-black text-siemens-deep-blue dark:text-white flex items-center gap-2">
                            <Award className="w-5 h-5 text-siemens-petrol" />
                            Team Performance
                        </h2>
                        <p className="text-xs text-siemens-text-soft mt-1 text-left">Owner workload and effectiveness (all-time)</p>
                    </div>
                    <ChevronDown className={cn("w-5 h-5 text-siemens-text-soft transition-transform duration-200", teamPerformanceExpanded && "rotate-180")} />
                </button>
                {teamPerformanceExpanded && (
                    <>
                        {/* Lane Scope Selector */}
                        <div className="px-6 py-3 border-t border-siemens-deep-blue/5 dark:border-[#3d3d4d] flex items-center gap-3">
                            <span className="text-xs font-semibold text-siemens-text-soft">Show:</span>
                            <div className="flex rounded-lg border border-siemens-deep-blue/10 dark:border-[#3d3d4d] overflow-hidden p-0.5 bg-slate-50 dark:bg-[#13131a]">
                                {[
                                    { value: 'active' as LaneScope, label: 'Active Only', desc: 'Now + Next' },
                                    { value: 'includeLater' as LaneScope, label: 'All Assigned', desc: '+ Later' },
                                    { value: 'laterOnly' as LaneScope, label: 'Later Only', desc: 'Backlog' },
                                ].map(option => (
                                    <button
                                        key={option.value}
                                        onClick={() => setLaneScope(option.value)}
                                        className={cn(
                                            "px-3 py-1.5 text-xs font-semibold rounded transition-all",
                                            laneScope === option.value
                                                ? "bg-siemens-petrol text-white shadow-sm"
                                                : "text-siemens-text-soft hover:text-siemens-deep-blue dark:hover:text-white"
                                        )}
                                        title={option.desc}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {/* Team Role Toggle */}
                        <div className="px-6 py-2 flex items-center gap-3 border-t border-siemens-deep-blue/5 dark:border-[#3d3d4d]">
                            <span className="text-xs font-semibold text-siemens-text-soft">View:</span>
                            <div className="flex rounded-lg border border-siemens-deep-blue/10 dark:border-[#3d3d4d] overflow-hidden p-0.5 bg-slate-50 dark:bg-[#13131a]">
                                {[
                                    { value: 'owner' as TeamRole, label: 'Owners', desc: 'Service Owners (SO)' },
                                    { value: 'architect' as TeamRole, label: 'Architects', desc: 'Solution Architects (ARQ)' },
                                ].map(option => (
                                    <button
                                        key={option.value}
                                        onClick={() => setTeamRole(option.value)}
                                        className={cn(
                                            "px-3 py-1.5 text-xs font-semibold rounded transition-all",
                                            teamRole === option.value
                                                ? "bg-siemens-petrol text-white shadow-sm"
                                                : "text-siemens-text-soft hover:text-siemens-deep-blue dark:hover:text-white"
                                        )}
                                        title={option.desc}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="overflow-x-auto border-t border-siemens-deep-blue/5 dark:border-[#3d3d4d]">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-slate-100 dark:bg-[#13131a] text-siemens-text-soft">
                                        <th className="px-4 py-3 text-left font-bold uppercase tracking-wider text-[10px]">
                                            {teamRole === 'architect' ? 'Architect' : 'Owner'}
                                        </th>
                                        <th className="px-4 py-3 text-center font-bold uppercase tracking-wider text-[10px]">
                                            {laneScope === 'laterOnly' ? 'Later Load' : laneScope === 'includeLater' ? 'Total Load' : 'Active Load'}
                                        </th>
                                        <th className="px-4 py-3 text-center font-bold uppercase tracking-wider text-[10px]">Effort</th>
                                        <th className="px-4 py-3 text-center font-bold uppercase tracking-wider text-[10px]">SGM %</th>
                                        <th className="px-4 py-3 text-center font-bold uppercase tracking-wider text-[10px]">Completed</th>
                                        <th className="px-4 py-3 text-center font-bold uppercase tracking-wider text-[10px]">Completion %</th>
                                        <th className="px-4 py-3 text-center font-bold uppercase tracking-wider text-[10px]">Value</th>
                                        <th className="px-4 py-3 text-center font-bold uppercase tracking-wider text-[10px]">Efficiency</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-siemens-deep-blue/5 dark:divide-[#3d3d4d]">
                                    {calculateOwnerMetrics(epics, users, laneScope, teamRole).map((owner) => {
                                        const loadLevel = getLoadLevel(owner.activeEpics);
                                        const loadColors = {
                                            low: 'bg-slate-200 dark:bg-slate-700',
                                            balanced: 'bg-siemens-feedback-green',
                                            high: 'bg-siemens-feedback-orange',
                                            critical: 'bg-red-500'
                                        };
                                        return (
                                            <tr key={owner.userId} className="hover:bg-siemens-deep-blue-8/20 dark:hover:bg-white/5">
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        {owner.photoUrl ? (
                                                            <img src={owner.photoUrl} alt="" className="w-7 h-7 rounded-full object-cover" />
                                                        ) : (
                                                            <div className="w-7 h-7 rounded-full bg-siemens-petrol/10 text-siemens-petrol flex items-center justify-center text-[10px] font-bold">
                                                                {owner.displayName.slice(0, 2).toUpperCase()}
                                                            </div>
                                                        )}
                                                        <span className="font-semibold text-siemens-deep-blue dark:text-white whitespace-nowrap">{owner.displayName}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <div className="w-16 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                                            <div className={cn("h-full rounded-full", loadColors[loadLevel])} style={{ width: `${Math.min(owner.activeEpics * 10, 100)}%` }} />
                                                        </div>
                                                        <span className="text-xs font-bold w-4">{owner.activeEpics}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-center font-bold text-siemens-deep-blue dark:text-white">{owner.totalEffort}</td>
                                                <td className="px-4 py-3 text-center">
                                                    {owner.strategicPercent > 0 ? (
                                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-siemens-petrol/10 text-siemens-petrol text-[10px] font-bold">
                                                            <ShieldCheck className="w-3 h-3" />{owner.strategicPercent}%
                                                        </span>
                                                    ) : (
                                                        <span className="text-siemens-text-soft">—</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className="inline-flex items-center gap-1 text-siemens-feedback-green font-bold">
                                                        <CheckCircle2 className="w-3.5 h-3.5" />{owner.completedEpics}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <div className="flex items-center justify-center gap-1">
                                                        <div className="w-12 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                                            <div className="h-full rounded-full bg-siemens-feedback-green" style={{ width: `${owner.completionPercent}%` }} />
                                                        </div>
                                                        <span className={cn("text-xs font-bold",
                                                            owner.completionPercent >= 50 ? "text-siemens-feedback-green" :
                                                                owner.completionPercent >= 25 ? "text-siemens-feedback-orange" :
                                                                    "text-siemens-text-soft"
                                                        )}>{owner.completionPercent}%</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-center font-bold text-siemens-petrol">{owner.valueDelivered}</td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className={cn("font-bold",
                                                        owner.efficiency >= 3 ? "text-siemens-feedback-green" :
                                                            owner.efficiency >= 2 ? "text-siemens-feedback-orange" :
                                                                "text-siemens-text-soft"
                                                    )}>{owner.efficiency.toFixed(1)}</span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {calculateOwnerMetrics(epics, users, laneScope, teamRole).length === 0 && (
                            <div className="p-8 text-center text-siemens-text-soft">
                                <Award className="w-8 h-8 mx-auto mb-2 opacity-30" />
                                <p className="text-sm">No owner data available</p>
                            </div>
                        )}
                    </>
                )}
            </motion.div>

            {/* Quick Actions removed - moved to Settings Page */}
        </div >
    );
}

export default ExecutiveView;
